import { AfterViewInit, Component, DestroyRef, EventEmitter, Input, OnInit, Output, forwardRef, inject, signal } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { PhoneNumberValidator } from './phone-number-validator';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { countryDropdown } from './countries-list.model';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs';
import { RxInputModule } from '../input/input.module';
import { RxSelectModule } from '../select';
import { MandatoryDirectiveModule } from '../../directives';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  standalone: true,
  selector: 'rx-applications-phone-number-input',
  templateUrl: './phone-number-input.component.html',
  styleUrls: ['./phone-number-input.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, FormsModule, RxInputModule, RxSelectModule, MandatoryDirectiveModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneNumberInputComponent),
      multi: true,
    },
  ],
})
export class PhoneNumberInputComponent implements ControlValueAccessor, OnInit, AfterViewInit {
  @Input() filterNoResultMessage = '';
  @Input() prefixLabel = 'Prefix';
  @Input() prefixPlaceholder = 'GB +44';
  @Input() phoneLabel = 'Phone number';
  @Input() errorMessage: string;
  @Input() phoneNumberControl: FormControl;
  @Output() phoneChange = new EventEmitter<string>();

  selectedCountryControl: FormControl;
  countryDropdown = countryDropdown;
  lostFocusOneTime = false;
  #phoneNumberUtil = PhoneNumberUtil.getInstance();
  #destroyRef = inject(DestroyRef);
  #phoneControlFocused = signal(false);

  ngOnInit(): void {
    this.selectedCountryControl = new FormControl(null, Validators.required);
    this.phoneNumberControl?.setValidators([Validators.required, PhoneNumberValidator(this.selectedCountryControl.value)]);
    this.autoFormatPhoneNumber();
  }

  ngAfterViewInit(): void {
    this.#setupPhoneNumberFormattingOnValueChanges();
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange = (value: unknown) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = (value: unknown) => {};

  writeValue(value: string): void {
    this.phoneNumberControl.setValue(value);
  }

  registerOnChange(fn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn): void {
    this.onTouched = fn;
  }

  countryChanged(): void {
    this.phoneNumberControl.setValidators([Validators.required, PhoneNumberValidator(this.selectedCountryControl.value)]);
    this.phoneNumberControl.updateValueAndValidity();
    this.#emitChange();
  }

  phoneInputChangeFocus($event: FocusEvent): void {
    if ($event.type === 'focus') {
      this.#phoneControlFocused.set(true);
      return;
    }

    if (this.lostFocusOneTime === false && $event.type === 'blur') {
      this.lostFocusOneTime = true;
    }

    this.#phoneControlFocused.set(false);
    this.autoFormatPhoneNumber();
    this.#emitChange();
  }

  get placeHolderForCountry(): string {
    const regionCode = this.selectedCountryControl.value ?? 'GB';
    return this.#phoneNumberUtil.getExampleNumber(regionCode).getNationalNumberOrDefault().toString();
  }

  get showError(): boolean {
    const { touched, invalid } = this.phoneNumberControl;
    return invalid && touched;
  }

  #emitChange(): void {
    const phoneNumber = this.#parsePhoneNumber(this.phoneNumberControl.value, this.selectedCountryControl.value);
    if (this.phoneNumberControl.valid && phoneNumber && this.selectedCountryControl.value) {
      const phoneNumberInternational = `+${phoneNumber.getCountryCode()}${phoneNumber.getNationalNumberOrDefault()}`;
      this.phoneChange.emit(phoneNumberInternational);
    }
  }

  #setToNationalPhoneNumber(): void {
    const phoneNumber = this.#parsePhoneNumber(this.phoneNumberControl.value);
    if (phoneNumber) {
      this.phoneNumberControl.patchValue(phoneNumber.getNationalNumberOrDefault().toString(), { emitEvent: false });
    }
  }

  #autoDetectRegionCode(): void {
    const phoneNumber = this.#parsePhoneNumber(this.phoneNumberControl.value);
    if (phoneNumber) {
      const newRegionCode = this.#phoneNumberUtil.getRegionCodeForCountryCode(phoneNumber.getCountryCode());
      this.selectedCountryControl.patchValue(newRegionCode);
      this.countryChanged();
    }
  }

  #parsePhoneNumber(inputValue: string, regionCode?: string): libphonenumber.PhoneNumber | null {
    try {
      return this.#phoneNumberUtil.parse(inputValue, regionCode);
    } catch (e) {
      return null;
    }
  }

  #setupPhoneNumberFormattingOnValueChanges(): void {
    // when the phone control is not focused, auto format the phone number immediately
    this.phoneNumberControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter(() => !this.#phoneControlFocused()),
        distinctUntilChanged(),
      )
      .subscribe(() => this.autoFormatPhoneNumber());

    // when the phone control is focused, format the phone number after 1 second of inactivity
    this.phoneNumberControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter(() => this.#phoneControlFocused()),
        debounceTime(1000),
        distinctUntilChanged(),
      )
      .subscribe(() => this.autoFormatPhoneNumber());  
  }

  autoFormatPhoneNumber(): void {
    this.#autoDetectRegionCode();
    this.#setToNationalPhoneNumber();
  }
}
